import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

/**
 * Initialize Sentry error tracking
 */
export function initSentry() {
  // Solo inicializar en producción o si está explícitamente habilitado
  const isDev = import.meta.env.DEV;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const forceDev = import.meta.env.VITE_SENTRY_FORCE_DEV === 'true' || import.meta.env.VITE_SENTRY_FORCE_DEV === true;

  if (!dsn) {
    console.warn('[Sentry] DSN not configured, skipping initialization');
    return;
  }

  // Prevent double initialization
  if (Sentry.isInitialized()) {
    console.log('[Sentry] Already initialized');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',

    // Integrations
    integrations: [
      // React Router integration for better error context
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),

      // Session Replay - captura qué hizo el usuario antes del error
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true, // Protect sensitive input data
      }),

      // Browser Tracing para performance
      Sentry.browserTracingIntegration(),
    ],

    // Performance Monitoring
    tracesSampleRate: isDev ? 1.0 : 0.1, // 100% en dev, 10% en prod

    // Session Replay
    replaysSessionSampleRate: isDev ? 1.0 : 0.1, // 10% de sesiones normales
    replaysOnErrorSampleRate: 1.0, // 100% de sesiones con errores

    // Error filtering
    beforeSend(event, hint) {
      // No enviar errores en desarrollo a menos que se fuerce
      if (isDev && !forceDev) {
        console.log('[Sentry] Event captured (dev mode, not sent):', event);
        return null;
      }

      // Filtrar errores conocidos/no relevantes
      const error = hint.originalException;
      if (error && typeof error.message === 'string') {
        // Ignorar errores de extensiones del navegador
        if (error.message.includes('chrome-extension://')) {
          return null;
        }
        // Ignorar errores de ad blockers
        if (error.message.includes('adsbygoogle')) {
          return null;
        }
      }

      return event;
    },

    // Breadcrumbs configuration
    maxBreadcrumbs: 50,
  });

  console.log(`[Sentry] Initialized successfully (${isDev ? 'Dev' : 'Prod'}${forceDev ? ' - Forced' : ''})`);
}

/**
 * Manually capture an error with additional context
 */
export function captureError(error, context = {}) {
  // If it's a string, convert to Error object
  const errorObject = typeof error === 'string' ? new Error(error) : error;

  Sentry.captureException(errorObject, {
    extra: context,
    // Add a fingerprint to group persistent crashes if needed
    fingerprint: context.isRestoredCrash ? ['restored-crash', context.crashType] : undefined,
  });
}

/**
 * Add breadcrumb for tracking user actions
 */
export function addBreadcrumb(message, data = {}) {
  Sentry.addBreadcrumb({
    message,
    level: 'info',
    data,
  });
}

/**
 * Set user context (optional, useful for identifying users)
 */
export function setUserContext(userId, email = null) {
  Sentry.setUser({
    id: userId,
    email,
  });
}

/**
 * HOC: Error Boundary component
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;
